import { useState } from "react";
import { ExternalLink, PlayCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { youtubeEmbedUrl, youtubeThumbnailUrl, youtubeWatchUrl } from "@/lib/youtube";

interface YouTubeEmbedProps {
  videoId: string;
  title: string;
  /** Click-to-play thumbnail first (better for pages with multiple videos). */
  lazy?: boolean;
  className?: string;
}

export function YouTubeEmbed({
  videoId,
  title,
  lazy = true,
  className,
}: YouTubeEmbedProps) {
  const [active, setActive] = useState(!lazy);
  return (
    <div className={cn("relative aspect-video w-full overflow-hidden bg-black", className)}>
      {active ? (
        <iframe
          src={youtubeEmbedUrl(videoId, { autoplay: lazy })}
          title={title}
          className="absolute inset-0 h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          className="group absolute inset-0 h-full w-full"
          aria-label={`Play: ${title}`}
        >
          <img
            src={youtubeThumbnailUrl(videoId)}
            alt=""
            className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
          <div className="absolute inset-0 grid place-items-center">
            <div className="grid size-14 place-items-center rounded-full bg-red-600 text-white shadow-lg transition-transform group-hover:scale-110">
              <PlayCircle className="size-8 fill-white text-white" />
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

interface YouTubeEmbedFooterProps {
  videoId: string;
  channel?: string;
  channelUrl?: string;
}

export function YouTubeEmbedFooter({ videoId, channel, channelUrl }: YouTubeEmbedFooterProps) {
  return (
    <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
      {channel ? (
        channelUrl ? (
          <a href={channelUrl} target="_blank" rel="noreferrer noopener" className="hover:text-foreground">
            {channel}
          </a>
        ) : (
          <span>{channel}</span>
        )
      ) : (
        <span>YouTube</span>
      )}
      <a
        href={youtubeWatchUrl(videoId)}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        Open on YouTube <ExternalLink className="size-3" />
      </a>
    </div>
  );
}
